import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";

// Keep in sync with SHOW_TESTIMONIALS in App.js. When testimonials are
// re-enabled, add `{ label: "Voices", id: "testimonials" }` back to this list.
const SHOW_TESTIMONIALS = false;

const links = [
  { label: "Work", id: "portfolio" },
  { label: "Approach", to: "/approach" },
  { label: "People", id: "team" },
  { label: "Careers", id: "careers" },
  ...(SHOW_TESTIMONIALS ? [{ label: "Voices", id: "testimonials" }] : []),
];

export default function Navbar() {
  const [overHero, setOverHero] = useState(false);
  const [heroRevealed, setHeroRevealed] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Track whether the navbar currently sits over the hero section.
  useEffect(() => {
    const hero = document.getElementById("top");
    if (!hero) {
      setOverHero(false);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => setOverHero(entry.isIntersecting),
      { rootMargin: "-80px 0px 0px 0px", threshold: 0 }
    );
    io.observe(hero);
    return () => io.disconnect();
  }, [location.pathname]);

  // The hero starts on a plain cream background and only becomes photographic
  // (dark) once its reveal animation fires. Listen for that so nav text only
  // turns light when there is actually a dark background behind it.
  useEffect(() => {
    const onReveal = () => setHeroRevealed(true);
    window.addEventListener("hero:revealed", onReveal);
    return () => window.removeEventListener("hero:revealed", onReveal);
  }, []);

  useEffect(() => {
    setHeroRevealed(false);
  }, [location.pathname]);

  // Light text only when over the dark, revealed hero. Otherwise dark text.
  const light = overHero && heroRevealed;

  // When arriving at "/" with a hash, scroll to it.
  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 50);
      }
    }
  }, [location]);

  const handleNav = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        overHero
          ? "bg-transparent"
          : "backdrop-blur-xl bg-[#F7F5F0]/75 border-b border-[#D8D4C7]/60"
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo-link"
          className="flex items-center gap-2 group"
        >
          <span className="w-2.5 h-2.5 rounded-full bg-[#E26D5C] inline-block group-hover:bg-[#F2C14E] transition-colors duration-500" />
          <span
            className={`font-display text-2xl font-light tracking-tighter transition-colors duration-300 ${
              light ? "text-[#F7F5F0]" : "text-[#1C1C19]"
            }`}
          >
            <span className="italic">Ground</span>Work.
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => {
            const cls = `link-underline text-sm font-medium transition-colors duration-300 ${
              light
                ? "text-[#F7F5F0]/90 hover:text-[#F7F5F0]"
                : "text-[#1C1C19]/80 hover:text-[#1C1C19]"
            }`;
            return l.to ? (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={cls}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.id}
                href={`/#${l.id}`}
                onClick={(e) => handleNav(e, l.id)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={cls}
              >
                {l.label}
              </a>
            );
          })}
        </nav>

        <a
          href="/#contact"
          onClick={(e) => handleNav(e, "contact")}
          data-testid="nav-cta-button"
          className="hidden md:inline-flex btn-magnetic items-center gap-2 px-5 py-2.5 rounded-full bg-[#1C1C19] text-[#F7F5F0] text-sm font-medium hover:bg-[#E26D5C]"
        >
          Build with us
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#F2C14E]" />
        </a>

        <button
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 rounded-full bg-[#1C1C19] text-[#F7F5F0]"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#F7F5F0] border-t border-[#D8D4C7]">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) =>
              l.to ? (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="font-display text-3xl text-[#1C1C19]"
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.id}
                  href={`/#${l.id}`}
                  onClick={(e) => handleNav(e, l.id)}
                  className="font-display text-3xl text-[#1C1C19]"
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              )
            )}
            <a
              href="/#contact"
              onClick={(e) => handleNav(e, "contact")}
              data-testid="mobile-nav-cta"
              className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#E26D5C] text-white"
            >
              Build with us
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
